import React, { useCallback, useState } from "react";

import { FormInstance } from "antd/es/form/hooks/useForm";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isThisYear } from "date-fns";
import { defineMessages, useIntl } from "react-intl";

import { Button, Dropdown, FormItem } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { ClearableDatePicker } from "@app/component/input/DatePicker";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";

import FormValues from "../../types/FormValues";
import FollowUpTimeMenu from "../FollowUpTimeMenu";

import styles from "./ActivityDate.module.scss";

interface Props {
  disabledTooltip?: string;
  form: FormInstance<FormValues>;
  isFollowUp?: boolean;
  onChange?: () => void;
}

const messages = defineMessages({
  changeDateTooltip: {
    id: "createActivityModal.footer.scheduleActivity.date.tooltip",
    defaultMessage: "{hasDate, select, true {Change Date} other {Set Date}}",
    description: "Date field tooltip",
  },
  dateFormat: {
    id: "createEditEntity.activity.date.format",
    defaultMessage: "{currentYear, select, true {eeee, MMM d} other {eeee, MMM d, yyyy}}",
    description: "Date format for current date",
  },
  followUpIn: {
    id: "createEditEntity.activity.date.followUpIn",
    defaultMessage: "Follow-up in",
    description: "Follow up in dropdown title",
  },
  noDate: {
    id: "createActivityModal.footer.scheduleActivity.noDate",
    defaultMessage: "No date",
    description: "No date",
  },
  removeDateTime: {
    id: "createActivityModal.footer.scheduleActivity.startDateTime.remove",
    defaultMessage: "Remove",
    description: "Remove Date and Time",
  },
});

const ActivityDate = ({ disabledTooltip, form, isFollowUp, onChange }: Props) => {
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [isCustomDate, setIsCustomDate] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setTooltipVisible(false);
      }
      setOpen(open);
    },
    [setOpen, setTooltipVisible]
  );

  const handleClearDateClick = useCallback(() => {
    form.setFieldsValue({
      endAt: undefined,
      startAt: undefined,
    });

    setTooltipVisible(false);
    setOpen(false);

    onChange?.();
  }, [form, onChange, setOpen, setTooltipVisible]);

  const handleClick = useCallback(() => {
    setTooltipVisible(false);
    setOpen(true);
  }, [setTooltipVisible, setOpen]);

  const dateValue = form.getFieldValue(ActivityFieldName.START_AT);
  const currentDate = isValidDate(dateValue) ? dateValue : undefined;
  const isCurrentYear = currentDate && isThisYear(currentDate);
  const dateFormat = intl.formatMessage(messages.dateFormat, { currentYear: isCurrentYear });

  return (
    <div className={styles.container}>
      <Dropdown
        arrow={false}
        dropdownRender={() => (
          <>
            <div className={styles.followUpTitle}>{intl.formatMessage(messages.followUpIn)}</div>
            <FollowUpTimeMenu form={form} onSetCustom={setIsCustomDate} />
          </>
        )}
        onOpenChange={handleOpenChange}
        open={open && isFollowUp && !isCustomDate}
        overlayClassName={styles.followUpDropdown}
        placement="top"
        trigger={isFollowUp && !isCustomDate ? ["click"] : []}
      >
        <Tooltip
          destroyTooltipOnHide
          mouseLeaveDelay={0}
          onOpenChange={setTooltipVisible}
          open={tooltipVisible}
          overlayClassName={styles.tooltip}
          placement="top"
          title={
            disabledTooltip ??
            intl.formatMessage(messages.changeDateTooltip, {
              hasDate: !!dateValue,
            })
          }
          trigger={open ? [] : ["hover"]}
        >
          <div>
            <FormItem name={ActivityFieldName.START_AT} noStyle>
              <ClearableDatePicker
                allowClear={false}
                bordered={false}
                disabled={!!disabledTooltip}
                dropdownClassName={styles.panel}
                inputReadOnly
                onOpenChange={handleOpenChange}
                open={open && (!isFollowUp || isCustomDate)}
                placeholder={intl.formatMessage(messages.noDate)}
                renderExtraFooter={() => (
                  <ButtonLink className={styles.clear} onClick={handleClearDateClick}>
                    {intl.formatMessage(messages.removeDateTime)}
                  </ButtonLink>
                )}
                showTime={false}
                suffixIcon={<></>}
              />
            </FormItem>

            <Button
              className={cn(styles.btn, {
                [styles.active]: open,
                [styles.hasValue]: !!currentDate,
              })}
              disabled={!!disabledTooltip}
              onClick={handleClick}
              type="control"
            >
              {currentDate
                ? formatDate(currentDate, dateFormat)
                : intl.formatMessage(messages.noDate)}
            </Button>
          </div>
        </Tooltip>
      </Dropdown>
    </div>
  );
};

export default ActivityDate;
